import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { capitalize } from 'lodash';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

import LoadingButton from '../../../../shared/components/LoadingButton/LoadingButton';
import Segment from '../../interfaces/Segment';
import SegmentNames from '../../interfaces/SegmentNames';
import styles from './RFMConfigForm.module.scss';

const defaultValues: Segment = {
  id: '',
  name: '',
  description: '',
  isPositive: true,
  recency: [1, 100],
  frequency: [1, 100],
  monetary: [1, 100],
};

const isPositiveLabels = [
  {
    label: 'Good',
    value: true,
  },
  {
    label: 'Bad',
    value: false,
  },
];

interface RMFConfigFormProps {
  segments: Segment[];
  config?: Segment;
  errorElement: JSX.Element | null;
  onClose: () => void;
  onUpdate: (config: Segment) => Promise<void>;
}

const RMFConfigForm: FC<RMFConfigFormProps> = ({
  segments,
  config,
  onClose,
  onUpdate,
  errorElement,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = useForm<Segment>({
    defaultValues: config || defaultValues,
  });

  const segmentName = config?.name || '';

  const validateSegmentName = (name: string) => {
    if (name === segmentName) return true;

    for (const segment of segments)
      if (segment.name.trim() === name.trim())
        return `'${name}' already exists.`;
    return true;
  };

  return (
    <>
      <DialogContent dividers>
        {errorElement}
        <Box
          component='form'
          id={styles.rmfConfigForm}
          onSubmit={handleSubmit(onUpdate)}
        >
          <TextField
            label='Segment Name'
            fullWidth
            {...register('name', {
              required: 'Segment Name is required.',
              validate: validateSegmentName,
            })}
            disabled={isSubmitting}
            autoFocus
            error={!!errors.name}
            helperText={errors.name?.message}
          />
          <TextField
            label='Description'
            fullWidth
            {...register('description')}
            disabled={isSubmitting}
            sx={{ mt: 2 }}
            multiline
            minRows={2}
          />
          {SegmentNames.map((segment) => (
            <Box key={segment} className={styles.segmentFields}>
              <TextField
                label={`${capitalize(segment)} Min`}
                type='number'
                {...register(`${segment}.0`, {
                  min: {
                    value: 0,
                    message: `${capitalize(segment)} must be positive`,
                  },
                  setValueAs: (value: string) =>
                    value !== '' ? parseInt(value) : null,
                })}
                disabled={isSubmitting}
                error={!!errors[segment]?.[0]}
                helperText={errors[segment]?.[0]?.message}
              />
              <TextField
                label={`${capitalize(segment)} Max`}
                type='number'
                {...register(`${segment}.1`, {
                  min: {
                    value: 0,
                    message: `${capitalize(segment)} must be positive`,
                  },
                  setValueAs: (value: string) =>
                    value !== '' ? parseInt(value) : null,
                  validate: (maxValue, formValues) => {
                    const minValue = formValues[segment]?.[0];
                    if (minValue !== null && maxValue !== null && maxValue < minValue) {
                      return `Max should be >= Min`;
                    }
                    return true;
                  },
                })}
                disabled={isSubmitting}
                error={!!errors[segment]?.[1]}
                helperText={errors[segment]?.[1]?.message}
              />
            </Box>
          ))}
          <Box className={styles.isPositiveContainer}>
            <Typography variant='body2' className={styles.isPositiveLabel}>
              Rise in the number of customers in this particular segment
            </Typography>
            <Controller
              name='isPositive'
              control={control}
              render={({ field: { value, onChange } }) => (
                <ToggleButtonGroup
                  value={value}
                  color='primary'
                  fullWidth
                  exclusive
                  onChange={(_, isPositive: boolean | null) =>
                    isPositive !== null && onChange(isPositive)
                  }
                >
                  {isPositiveLabels.map((isPositiveLabel) => (
                    <ToggleButton
                      className={styles.switchButton}
                      value={isPositiveLabel.value}
                      key={isPositiveLabel.label}
                    >
                      {isPositiveLabel.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isSubmitting}
          variant='text'
          color='secondary'
          onClick={onClose}
        >
          Cancel
        </Button>
        <LoadingButton onClick={handleSubmit(onUpdate)} loading={isSubmitting}>
          Save
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default RMFConfigForm;
